'use client';

import { Img } from '@/components/blocks/MaterialImage';
import { gallerySX } from '@/components/blocks/ProductDetails/styles/gallery';
import { productImageBaseSX } from '@/components/blocks/ProductImage/styles/base';
import { ImageMagnify } from '@/components/blocks/ProductImage/styles/imageMagnify';
import { productImageSelectedSX } from '@/components/blocks/ProductImage/styles/selected';
import { productImageTableCellSX } from '@/components/blocks/ProductImage/styles/tableCell';
import { productImageThumbnailSX } from '@/components/blocks/ProductImage/styles/thumbnail';
import { combineSX } from '@/utils/combineSX';
import { Box, SxProps, Theme } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { CDN_BASE_URL } from 'shared/custom/constants/commonConstants';

type Props = {
	src: string;
	alt: string;
	isThumbnail?: boolean;
	isSelected?: boolean;
	sx?: SxProps<Theme>;
	canZoom?: boolean;
	gtmSelectItem?: () => void;
	mobileZoom?: boolean;
	imagePortalId?: string;
	width?: number;
	height?: number;
	loading?: 'eager' | 'lazy';
	fetchPriority?: 'high' | 'low' | 'auto';
	/** Set on the LCP candidate only — the first gallery slide or the lone gallery image. */
	priority?: boolean;
};

export const ProductImage: React.FC<Props & { onError?: () => void }> = ({
	src,
	onError,
	alt,
	isSelected,
	isThumbnail,
	sx,
	canZoom,
	gtmSelectItem,
	imagePortalId = 'mediaImageZoom',
	width,
	height,
	loading,
	fetchPriority,
	priority = false,
}) => {
	const [hasError, setHasError] = useState(false);
	const [isLoaded, setIsLoaded] = useState(false);
	const imgRef = useRef<HTMLImageElement>(null);

	useEffect(() => {
		setHasError(false);
		/**
		 * On SSR the browser can finish loading (and decoding) the <img> before
		 * hydration attaches the `onLoad` listener, so that native `load` event
		 * is missed and never fires again. Check `complete` here to catch that
		 * case instead of leaving `isLoaded` stuck at false forever.
		 */
		setIsLoaded(!!imgRef.current?.complete && (imgRef.current?.naturalWidth ?? 0) > 0);
	}, [src]);

	if (!src || hasError) return null;

	const handleImageError = () => {
		setHasError(true);
		if (onError) onError();
	};

	const handleImageLoad = () => setIsLoaded(true);
	/**
	 * Secondary images stay hidden until they load, so a half-decoded image never shows.
	 * The LCP image must never be hidden: `isLoaded` only flips from the React `onLoad`
	 * prop, which is not attached until hydration, so gating it would stop the browser
	 * recording LCP until hydration finishes — regardless of when the bytes arrived.
	 */
	const loadingVisibilitySx: SxProps<Theme> = priority ? {} : { opacity: isLoaded ? 1 : 0 };

	const zoomImagePath = (): string => {
		const splitSrc = src.split('/');
		const [fileName, ext] = splitSrc[splitSrc.length - 1].split('.');
		const [baseFileName, suffix] = fileName.split('-');
		const baseUrl = `${CDN_BASE_URL}/${splitSrc[3]}/${splitSrc[4]}`;
		if (suffix === 'm' || suffix === 'd') return `${baseUrl}/${baseFileName}-z.${ext}`;
		return `${baseUrl}/${baseFileName}-z${suffix}.${ext}`;
	};

	if (canZoom)
		return (
			<Box sx={combineSX([gallerySX, loadingVisibilitySx])}>
				<ImageMagnify
					{...{
						smallImage: {
							alt,
							isFluidWidth: true,
							src,
							onLoad: handleImageLoad,
							onError: handleImageError,
						},
						largeImage: {
							src: zoomImagePath(),
							width: 1200,
							height: 1200,
							onError: handleImageError,
						},
						enlargedImagePortalId: imagePortalId,
						enlargedImageContainerStyle: {
							zIndex: '1500',
						},
						enlargedImageContainerDimensions: {
							width: '100%',
							height: '100%',
						},
					}}
				/>
			</Box>
		);

	return (
		<Box onClick={gtmSelectItem}>
			<Img
				ref={imgRef}
				sx={combineSX([
					productImageBaseSX,
					isThumbnail && productImageThumbnailSX,
					isSelected && productImageSelectedSX,
					sx,
					loadingVisibilitySx,
				])}
				src={src}
				alt={alt}
				width={width}
				height={height}
				loading={loading}
				fetchPriority={fetchPriority}
				onLoad={handleImageLoad}
				onError={handleImageError}
			/>
		</Box>
	);
};

export const TableCellProductImage: React.FC<Props> = ({
	src,
	alt,
	isSelected,
	isThumbnail,
	gtmSelectItem,
	sx,
}) => (
	<ProductImage
		sx={combineSX([productImageTableCellSX, sx])}
		src={src}
		alt={alt}
		isSelected={isSelected}
		isThumbnail={isThumbnail}
		gtmSelectItem={gtmSelectItem}
	/>
);
